import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import Title from 'components/curo-components/common/Title';
import { CuroAppRoute } from 'enums/curo/CuroAppRoute';
import { EnvironmentVariable } from 'enums/curo/EnvironmentVariable';
import { envs } from 'envs';
import { resetApplication } from 'handlers/curo-handlers/applicationProcessSlice';
import { getSignInProcessState } from 'handlers/curo-handlers/selectors';
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks';
import { useAppProgressBar } from 'hooks/useAppProgressBar';
import CuroAuthorizedLayout from 'layout/CuroAuthorizedLayout';
import CuroMainLayout from 'layout/CuroMainLayout';
import { ApplyNowIcon, FindBranchIcon, LoginIcon, ResumeApplicationIcon } from 'static/images';
import styled from 'types/AppTheme';

const StyledLinksWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  justify-content: flex-start;
`;

const StyledHomePageLink = (element: 'a' | typeof Link) => styled(element)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 42px;
  padding: 36px 24px;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid #e7e9ec;
  border-radius: 12px;
  width: min-content;
  min-width: 250px;
  flex-grow: 1;
  flex-basis: 250px;

  & > h5 {
    color: ${({ theme }) => theme.colors.accentColor};
  }
`;

const InnerHomePageLink = StyledHomePageLink(Link);
const ExternalHomePageLink = StyledHomePageLink('a');

const HomePage = () => {
  const { resetProgress } = useAppProgressBar();

  const dispatch = useAppDispatch();
  const { accountData } = useAppSelector(getSignInProcessState);

  const shouldShowAuthorizedPage = accountData;

  const applyNow = () => {
    dispatch(resetApplication());
  };

  useEffect(() => {
    resetProgress();
  }, []);

  const renderLinks = () => {
    return (
      <StyledLinksWrapper>
        <InnerHomePageLink onClick={applyNow} to={CuroAppRoute.ApplyProcess}>
          <ApplyNowIcon />
          <Title type="h5">Apply Now</Title>
        </InnerHomePageLink>
        <ExternalHomePageLink
          href={envs[EnvironmentVariable.FindBranchLink]}
          rel="noopener noreferrer"
          target="_blank"
        >
          <FindBranchIcon />
          <Title type="h5">Find Branch</Title>
        </ExternalHomePageLink>
        {!shouldShowAuthorizedPage && (
          <InnerHomePageLink to={CuroAppRoute.SignIn} rel="noopener noreferrer">
            <LoginIcon />
            <Title type="h5">Login</Title>
          </InnerHomePageLink>
        )}
        {shouldShowAuthorizedPage && (
          <InnerHomePageLink to={CuroAppRoute.ResumeProcess} rel="noopener noreferrer">
            <ResumeApplicationIcon />
            <Title type="h5">Resume Application</Title>
          </InnerHomePageLink>
        )}
      </StyledLinksWrapper>
    );
  };

  const renderPage = () => {
    if (shouldShowAuthorizedPage) {
      return <CuroAuthorizedLayout>{renderLinks()}</CuroAuthorizedLayout>;
    }

    return <CuroMainLayout>{renderLinks()}</CuroMainLayout>;
  };

  return renderPage();
};

export default HomePage;
